import { clientFetch } from '~/lib/.client/fetch';
import type { BuildEndpointResponse } from '~/routes/api.deploy.$project.($id)';
import type {
  DomainEntry,
  ListDNSRecordsActionResponse,
  GetSSLCertificateActionResponse,
  RemoveDomainAliasActionResponse,
  SetPrimaryDomainActionResponse,
  UpdateSEOBoostActionResponse,
} from '~/routes/api.domains.$action.($id)';

export async function buildOnNetlify(projectId: string, zipContent: Uint8Array) {
  const body = new FormData();

  body.append('zip', new Blob([zipContent], { type: 'application/zip' }), 'bolt-project.zip');
  body.append('title', 'Bolt.new build');

  const response = await clientFetch(`/api/deploy/${projectId}?build=true`, {
    method: 'PUT',
    body,
  });

  if (!response.ok) {
    const text = await response.text();

    throw new Error(text);
  }

  const responseBody = await response.json<BuildEndpointResponse>();

  return responseBody;
}

export async function deployOnNetlify(projectId: string, zipContent: Uint8Array) {
  const response = await clientFetch(`/api/deploy/${projectId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/zip',
    },
    body: zipContent,
  });

  if (!response.ok) {
    const text = await response.text();

    throw new Error(text);
  }

  return await response.json();
}

export async function listDomainEntries(projectId: string) {
  const response = await clientFetch(`/api/domains/dns_records/${projectId}`);

  let data: ListDNSRecordsActionResponse | undefined;

  try {
    data = await response.json<ListDNSRecordsActionResponse>();
  } catch {
    const info = await response.text();

    throw new Error(`Failed to list domain entries: ${info}`);
  }

  if (!data) {
    // if the response is empty, we return an empty array
    return { entries: [], seoBoost: false };
  }

  if (!data.success) {
    throw new Error(data.error);
  }

  return { entries: data.entries, seoBoost: data.seo_boost };
}

export async function getSSLCertificate(projectId: string) {
  const response = await clientFetch(`/api/domains/ssl/${projectId}`);

  let data: GetSSLCertificateActionResponse | undefined;

  try {
    data = await response.json<GetSSLCertificateActionResponse>();
  } catch {
    const info = await response.text();

    throw new Error(`Failed to get SSL status: ${info}`);
  }

  if (!data) {
    return { renew_running: false, domains: [] };
  }

  if (!data.success) {
    throw new Error(data.error);
  }

  return data.certificate;
}

export async function loadDNSStatus(domains: DomainEntry[]): Promise<DomainEntry[]> {
  return await Promise.all(
    domains.map(async (entry) => {
      const status = await getDNSStatus(entry.domain);

      if (status === 'not-configured' && entry.managedBy === 'BOLT') {
        // this means that the domain is managed by Netlify but is not yet resolving
        return { ...entry, status: 'pending' };
      }

      return { ...entry, status };
    }),
  );
}

async function getDNSStatus(domain: string): Promise<DomainEntry['status']> {
  const response = await clientFetch(`/api/domains/verify?domain=${domain}`);

  if (!response.ok) {
    return 'unknown';
  }

  const { results } = await response.json<{ results: Array<{ cdnTier: string; domain: string; result: boolean }> }>();

  const result = results.find((result: { domain: string }) => result.domain === domain);

  if (!result) {
    console.warn(`No DNS verification result found for domain ${domain}`);

    return 'unknown';
  }

  return result.result ? 'complete' : 'not-configured';
}

export async function removeDomain(projectId: string, domain: string) {
  const response = await clientFetch(`/api/domains/domain/${projectId}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ domain }),
  });

  let data: RemoveDomainAliasActionResponse | undefined;

  try {
    data = await response.json<RemoveDomainAliasActionResponse>();
  } catch {
    const info = await response.text();

    throw new Error(`Failed to list domain entries: ${info}`);
  }

  if (!data) {
    throw new Error(`Something went wrong while removing the domain ${domain}`);
  }

  if (!data.success) {
    throw new Error(data.error);
  }

  return {
    siteUrl: data.site_url,
    isCustomDomain: data.is_custom_domain,
    seoBoost: data.seo_boost,
    entries: data.entries,
  };
}

export async function setPrimaryDomain(projectId: string, domain: string) {
  const response = await clientFetch(`/api/domains/primary/${projectId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ domain }),
  });

  let data: SetPrimaryDomainActionResponse | undefined;

  try {
    data = await response.json<SetPrimaryDomainActionResponse>();
  } catch {
    const info = await response.text();

    throw new Error(`Failed to list domain entries: ${info}`);
  }

  if (!data) {
    throw new Error(`Something went wrong while removing the domain ${domain}`);
  }

  if (!data.success) {
    throw new Error(data.error);
  }

  return {
    siteUrl: data.site_url,
    isCustomDomain: data.is_custom_domain,
    seoBoost: data.seo_boost,
    entries: data.entries,
  };
}

export async function setSEOBoost(projectId: string, seoBoost: boolean) {
  const response = await clientFetch(`/api/domains/seo_boost/${projectId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ seoBoost }),
  });

  let data: UpdateSEOBoostActionResponse | undefined;

  try {
    data = await response.json<UpdateSEOBoostActionResponse>();
  } catch {
    // do nothing
  }

  if (!data || !data.success) {
    throw new Error(`We couldn't update, try again later`);
  }
}
